'use client';

import { useEffect, useState } from 'react';

interface CircularGaugeProps {
  title?: string;
  value?: number;
  total?: number;
  percentage?: number;
  filledColor?: string;
  backgroundArcColor?: string;
  valuePrefix?: string;
  totalPrefix?: string;
  totalSuffix?: string;
  decimalPlaces?: number;
  arcThickness?: number;
  valueTextColor?: string;
  valueFontFamily?: string;
  valueFontSize?: number;
  totalTextColor?: string;
  totalFontFamily?: string;
}

const CircularGauge = ({
  value = 100,
  total = 100,
  percentage = 60,
  filledColor = '#154735',
  backgroundArcColor = '#E5E5E5',
  valuePrefix = '$',
  decimalPlaces = 2,
  arcThickness = 10,
  valueTextColor = '#000',
  valueFontFamily = 'inherit',
  valueFontSize = 24,
}: CircularGaugeProps) => {
  const [mounted, setMounted] = useState(false);

  // Ensure we're rendering on client side for SVG calculations
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div className="h-64 w-full max-w-xs bg-gray-50"></div>;
  }

  // Normalize percentage to be between 0 and 100
  const normalizedPercentage = Math.min(100, Math.max(0, percentage));

  // SVG parameters
  const svgWidth = 210;
  const svgHeight = 120;
  const centerX = svgWidth / 2;
  const centerY = svgHeight - 10; // Move up slightly to leave room for the value
  const radius = 90;
  const strokeWidth = arcThickness;

  // Calculate the notch size
  const notchRadius = strokeWidth;

  // Calculate the angle for the arc based on percentage
  // For 100%, we need to make sure the notch aligns perfectly with the end of the arc
  let angle = 0;

  if (normalizedPercentage === 100) {
    // At 100%, we want the notch to be exactly at the end of the arc
    angle = 180;
  } else {
    // For other percentages, we scale normally
    angle = (normalizedPercentage / 100) * 180;
  }

  // Calculate the coordinates for the end point of the filled arc
  const endX = centerX + radius * Math.cos(((180 - angle) * Math.PI) / 180);
  const endY = centerY - radius * Math.sin(((180 - angle) * Math.PI) / 180);

  // Calculate the inner circle radius
  const innerCircleRadius = strokeWidth * 0.4;

  // Format the total value with the specified decimal places
  const formattedTotal = total.toFixed(decimalPlaces);

  return (
    <div className="mx-auto max-w-xs">
      <div className="relative">
        <svg width="100%" height="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid meet">
          {/* Background arc */}
          <path
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
            fill="none"
            stroke={backgroundArcColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          {/* Progress arc */}
          {normalizedPercentage > 0 && (
            <path
              d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`}
              fill="none"
              stroke={filledColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          )}

          {/* Notch at the end of the progress arc - show at all percentages > 0 */}
          {normalizedPercentage > 0 && (
            <>
              <circle cx={endX} cy={endY} r={notchRadius} fill={filledColor} />
              <circle cx={endX} cy={endY} r={innerCircleRadius} fill={backgroundArcColor} />
            </>
          )}

          {/* Center value - positioned at the bottom of the circle */}
          <text
            x={centerX}
            y={centerY - 10} // Position near the bottom of the circle
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={valueFontSize}
            fontFamily={valueFontFamily}
            fill={valueTextColor}
          >
            {valuePrefix}
            {value}
          </text>
        </svg>
      </div>

      {/* {total !== undefined && (
        <div className="p-2">
          <p
            className="text-center text-lg"
            style={{
              color: totalTextColor,
              fontFamily: totalFontFamily,
            }}
          >
            Total: {totalPrefix}
            {formattedTotal}
            {totalSuffix}
          </p>
        </div>
      )} */}
    </div>
  );
};

export default CircularGauge;
